/**
 * Ad insights: pure rules that read our own results and say what needs a person's decision.
 *
 * Nothing here pauses, changes or publishes anything. It only describes what it sees, and a manager answers
 * "Pause" or "Keep going". Unknown is never zero: a missing number means "not available" and stops a rule from firing.
 */
import { z } from "zod";
import type { AdChannel } from "./adsProducts.ts";

/** Starting values from LinkedIn creative-testing guidance and the team's Google rule. A manager can change them. */
export const INSIGHT_DEFAULTS = {
  /** Views each ad needs before its numbers are read. */
  minImpressions: 1000,
  /** A click rate under this, after enough views, is a weak ad (0.4%). */
  weakClickRate: 0.004,
  /** An ad this far below its own best week is getting tired (20%). */
  tiredDrop: 0.2,
  /** How many weeks in a row it stays that far below. */
  tiredWeeks: 2,
  /** Google: days a keyword or ad must run before "no sign-ups" is raised. */
  googleMinDays: 14,
  /** Google: spend with no sign-ups that raises it when there is no target cost per sign-up. */
  googleNoTargetSpend: 75,
  /** Google: with a target cost per sign-up, raise it at this many times the target. */
  googleCpaMultiple: 2,
  /** A test is "too close to call" when the click rates differ by less than this share of the higher one. */
  closeShare: 0.1,
  /** Days a "Keep going" answer is respected before we ask again. */
  snoozeDays: 7,
  /** LinkedIn works best with this many ads in a campaign. */
  adsPerCampaign: 4,
} as const;

export const insightSettingsSchema = z.object({
  minImpressions: z.number().int().min(100).max(1_000_000),
  weakClickRate: z.number().min(0.0005).max(0.2),
  tiredDrop: z.number().min(0.05).max(0.9),
  tiredWeeks: z.number().int().min(1).max(8),
  googleMinDays: z.number().int().min(3).max(90),
  googleNoTargetSpend: z.number().min(1).max(1_000_000),
  googleCpaMultiple: z.number().min(1).max(20),
  closeShare: z.number().min(0.01).max(0.5),
  snoozeDays: z.number().int().min(1).max(60),
  adsPerCampaign: z.number().int().min(2).max(15),
});
export type InsightSettings = z.infer<typeof insightSettingsSchema>;
export const DEFAULT_INSIGHT_SETTINGS: InsightSettings = { ...INSIGHT_DEFAULTS };

// --------------------------------------------------------------------------------------------------------------------
// Numbers
// --------------------------------------------------------------------------------------------------------------------

/** Clicks per view, or null when either number is missing or there were no views. */
export function clickRate(impressions: number | null, clicks: number | null): number | null {
  if (impressions === null || clicks === null || impressions <= 0) return null;
  return clicks / impressions;
}

/** 0.0031 -> "0.31%". Always two decimals for rates this small. */
export function percent(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

const n0 = (v: number) => v.toLocaleString("en-US");

// --------------------------------------------------------------------------------------------------------------------
// Alerts
// --------------------------------------------------------------------------------------------------------------------

export const ALERT_KINDS = ["weak", "tired", "no_signups"] as const;
export const alertKindSchema = z.enum(ALERT_KINDS);
export type AlertKind = (typeof ALERT_KINDS)[number];

/** One ad in one week. The same ad has one row per week. */
export interface AdWeekRow {
  /** Stable name for the ad: its draft id when known, otherwise its name. */
  adKey: string;
  adName: string;
  channel: AdChannel;
  weekStart: string;
  impressions: number | null;
  clicks: number | null;
}

export interface InsightAlert {
  /** Same alert, same key: so an answer given once is remembered. */
  key: string;
  kind: AlertKind;
  channel: AdChannel;
  adKey: string;
  /** What the ad or keyword is called. */
  title: string;
  /** Short facts, each a sentence a non-technical person can read. */
  facts: string[];
  /** Why this matters, in plain words. */
  why: string;
  /** The two choices. The first is the one the rule suggests. */
  suggest: "pause" | "new_angle";
}

interface AdTotals {
  adKey: string;
  adName: string;
  channel: AdChannel;
  impressions: number;
  clicks: number;
  /** Weeks where both numbers are known, oldest first. */
  weeks: Array<{ weekStart: string; impressions: number; clicks: number }>;
}

function totalsPerAd(rows: AdWeekRow[]): AdTotals[] {
  const by = new Map<string, AdTotals>();
  for (const r of rows) {
    if (r.impressions === null || r.clicks === null) continue;
    const k = `${r.channel}|${r.adKey}`;
    const cur = by.get(k) ?? { adKey: r.adKey, adName: r.adName, channel: r.channel, impressions: 0, clicks: 0, weeks: [] };
    cur.impressions += r.impressions;
    cur.clicks += r.clicks;
    const w = cur.weeks.find((x) => x.weekStart === r.weekStart);
    if (w) {
      w.impressions += r.impressions;
      w.clicks += r.clicks;
    } else cur.weeks.push({ weekStart: r.weekStart, impressions: r.impressions, clicks: r.clicks });
    by.set(k, cur);
  }
  for (const t of by.values()) t.weeks.sort((a, b) => a.weekStart.localeCompare(b.weekStart));
  return [...by.values()];
}

/** An ad with enough views and a click rate under the line. */
export function weakAdAlert(t: AdTotals, s: InsightSettings): InsightAlert | null {
  if (t.impressions < s.minImpressions) return null;
  const rate = t.clicks / t.impressions;
  if (rate >= s.weakClickRate) return null;
  return {
    key: `weak:${t.channel}:${t.adKey}`,
    kind: "weak",
    channel: t.channel,
    adKey: t.adKey,
    title: t.adName,
    facts: [`Click rate ${percent(rate)} after ${n0(t.impressions)} views`, `Our line is ${percent(s.weakClickRate)}`],
    why: "Few people click it. Pause it to stop spending on it, or keep it running if you want to give it more time.",
    suggest: "pause",
  };
}

/**
 * An ad whose click rate has stayed well below its own best week for several weeks running.
 * Each of those weeks needs enough views to be trusted, and the best week must come before them.
 */
export function tiredAdAlert(t: AdTotals, s: InsightSettings): InsightAlert | null {
  const solid = t.weeks.filter((w) => w.impressions >= s.minImpressions);
  if (solid.length < s.tiredWeeks + 1) return null;
  const rates = solid.map((w) => ({ weekStart: w.weekStart, rate: w.clicks / w.impressions }));
  const recent = rates.slice(-s.tiredWeeks);
  const before = rates.slice(0, rates.length - s.tiredWeeks);
  const peak = before.reduce((a, b) => (b.rate > a.rate ? b : a));
  if (peak.rate <= 0) return null;
  const line = peak.rate * (1 - s.tiredDrop);
  if (!recent.every((r) => r.rate <= line)) return null;
  const now = recent[recent.length - 1]!.rate;
  const down = Math.round((1 - now / peak.rate) * 100);
  return {
    key: `tired:${t.channel}:${t.adKey}`,
    kind: "tired",
    channel: t.channel,
    adKey: t.adKey,
    title: t.adName,
    facts: [
      `Click rate fell from ${percent(peak.rate)} to ${percent(now)} (${t.weeks.length >= 2 ? `${s.tiredWeeks} weeks in a row` : "recently"})`,
      `${down}% below its best week`,
    ],
    why: "The same people keep seeing it. A new angle usually helps more than a new picture.",
    suggest: "new_angle",
  };
}

export interface GoogleItemRow {
  /** Keyword or ad text. */
  name: string;
  /** What kind of thing it is, for the sentence. */
  kind: "keyword" | "ad";
  /** Days it has been running. */
  days: number | null;
  clicks: number | null;
  conversions: number | null;
  spend: number | null;
  currency: string | null;
  /** The cost per sign-up we are aiming for, when known. */
  targetCpa: number | null;
}

/** The team's Google rule: after enough days and spend with no sign-ups, a person decides. */
export function googleNoSignupsAlert(r: GoogleItemRow, s: InsightSettings): InsightAlert | null {
  if (r.days === null || r.conversions === null || r.spend === null) return null;
  if (r.days < s.googleMinDays || r.conversions !== 0) return null;
  const line = r.targetCpa !== null && r.targetCpa > 0 ? r.targetCpa * s.googleCpaMultiple : s.googleNoTargetSpend;
  if (r.spend < line) return null;
  const money = (v: number) => `${r.currency === "CAD" ? "CA$" : "$"}${Math.round(v).toLocaleString("en-US")}`;
  const hasClicks = r.clicks !== null && r.clicks > 0;
  return {
    key: `no_signups:google_search:${r.kind}:${r.name.trim().toLowerCase()}`,
    kind: "no_signups",
    channel: "google_search",
    adKey: r.name,
    title: `${r.kind === "keyword" ? "Keyword" : "Ad"}: ${r.name}`,
    facts: [`${r.days} days running, ${money(r.spend)} spent, 0 sign-ups`],
    why: hasClicks
      ? `Our Google rule: look after ${s.googleMinDays} days and ${money(line)} with no sign-ups. It gets clicks, so check that sign-up tracking works before pausing.`
      : `Our Google rule: look after ${s.googleMinDays} days and ${money(line)} with no sign-ups.`,
    suggest: "pause",
  };
}

export function adAlerts(rows: AdWeekRow[], google: GoogleItemRow[], s: InsightSettings = DEFAULT_INSIGHT_SETTINGS): InsightAlert[] {
  const out: InsightAlert[] = [];
  for (const t of totalsPerAd(rows)) {
    // An ad that is both weak and tired is shown once, as weak: pausing it already answers the tired question.
    const a = weakAdAlert(t, s) ?? tiredAdAlert(t, s);
    if (a) out.push(a);
  }
  for (const g of google) {
    const a = googleNoSignupsAlert(g, s);
    if (a) out.push(a);
  }
  return out;
}

// --------------------------------------------------------------------------------------------------------------------
// Answers
// --------------------------------------------------------------------------------------------------------------------

export const alertAnswerSchema = z.enum(["paused", "kept"]);
export type AlertAnswer = z.infer<typeof alertAnswerSchema>;

export interface AlertDecision {
  key: string;
  answer: AlertAnswer;
  decidedAt: string;
}

const DAY = 86_400_000;

/** When "Keep going" runs out and we may ask again. Null for "Pause": that answer does not expire. */
export function revisitAt(answer: AlertAnswer, decidedAt: string, s: InsightSettings = DEFAULT_INSIGHT_SETTINGS): string | null {
  if (answer === "paused") return null;
  const t = Date.parse(decidedAt);
  if (!Number.isFinite(t)) return null;
  return new Date(t + s.snoozeDays * DAY).toISOString();
}

/** Alerts a person still has to answer: an answered one is hidden, and "Keep going" comes back after its days. */
export function openAlerts(alerts: InsightAlert[], decisions: AlertDecision[], now: string, s: InsightSettings = DEFAULT_INSIGHT_SETTINGS): InsightAlert[] {
  const latest = new Map<string, AlertDecision>();
  for (const d of decisions) {
    const cur = latest.get(d.key);
    if (!cur || d.decidedAt > cur.decidedAt) latest.set(d.key, d);
  }
  const nowMs = Date.parse(now);
  return alerts.filter((a) => {
    const d = latest.get(a.key);
    if (!d) return true;
    if (d.answer === "paused") return false;
    const back = revisitAt("kept", d.decidedAt, s);
    return back !== null && Number.isFinite(nowMs) && nowMs >= Date.parse(back);
  });
}

// --------------------------------------------------------------------------------------------------------------------
// Tests: one thing at a time
// --------------------------------------------------------------------------------------------------------------------

export const TEST_KINDS = ["format", "offer", "angle", "wording"] as const;
export const testKindSchema = z.enum(TEST_KINDS);
export type TestKind = (typeof TEST_KINDS)[number];
export const TEST_KIND_LABEL: Record<TestKind, string> = { format: "Format", offer: "Offer", angle: "Angle", wording: "Wording" };
/** The order to test in: the biggest differences first, small wording last. */
export const TEST_ORDER: readonly TestKind[] = TEST_KINDS;

export interface TestSide {
  label: string;
  impressions: number | null;
  clicks: number | null;
}

export type TestVerdict =
  | { status: "not_enough"; reason: string }
  | { status: "too_close"; reason: string }
  | { status: "leading"; reason: string; leader: "a" | "b"; ratesLabel: string };

/** Reads a two-way test. It never names a winner before both sides have enough views. */
export function readTest(a: TestSide, b: TestSide, s: InsightSettings = DEFAULT_INSIGHT_SETTINGS): TestVerdict {
  const ra = clickRate(a.impressions, a.clicks);
  const rb = clickRate(b.impressions, b.clicks);
  if (ra === null || rb === null) return { status: "not_enough", reason: "Not available yet" };
  if ((a.impressions ?? 0) < s.minImpressions || (b.impressions ?? 0) < s.minImpressions) {
    return { status: "not_enough", reason: `Needs ${n0(s.minImpressions)} views each` };
  }
  const hi = Math.max(ra, rb);
  const lo = Math.min(ra, rb);
  const ratesLabel = `${percent(ra)} vs ${percent(rb)}`;
  if (hi === 0 || (hi - lo) / hi < s.closeShare) return { status: "too_close", reason: "Too close to call" };
  const leader = ra > rb ? "a" : "b";
  return { status: "leading", leader, ratesLabel, reason: `${leader === "a" ? a.label : b.label} is ahead` };
}

// --------------------------------------------------------------------------------------------------------------------
// What we're testing: each test ad against the average of the other LinkedIn ads
// --------------------------------------------------------------------------------------------------------------------

export interface TestAdInput {
  adKey: string;
  adName: string;
  testKind: TestKind;
  impressions: number | null;
  clicks: number | null;
}

export interface TestRead {
  adKey: string;
  adName: string;
  testKind: TestKind;
  /** "1,840 views", or "Not available" when we have no figure. */
  viewsLabel: string;
  /** Its click rate, or "Not available". */
  rateLabel: string;
  status: "not_enough" | "too_close" | "ahead" | "behind";
  /** A short sentence a person can read, such as "Needs 1,000 views" or "Ahead of your other ads". */
  reason: string;
  /** "0.61% vs 0.43%" when a comparison was made. */
  ratesLabel: string | null;
}

const fmt = (n: number) => n.toLocaleString("en-US");

/**
 * Reads every test ad against the combined result of the other LinkedIn ads of the product. It never names a winner before
 * both sides have enough views, and it says "too close" when the difference is small.
 */
export function readTests(tests: TestAdInput[], others: Array<{ adKey: string; impressions: number | null; clicks: number | null }>, s: InsightSettings = DEFAULT_INSIGHT_SETTINGS): TestRead[] {
  return tests.map((t) => {
    const rest = others.filter((o) => o.adKey !== t.adKey && o.impressions !== null && o.clicks !== null);
    const baseline: TestSide = { label: "your other ads", impressions: rest.length ? rest.reduce((a, o) => a + (o.impressions ?? 0), 0) : null, clicks: rest.length ? rest.reduce((a, o) => a + (o.clicks ?? 0), 0) : null };
    const rate = clickRate(t.impressions, t.clicks);
    const base = { adKey: t.adKey, adName: t.adName, testKind: t.testKind, viewsLabel: t.impressions === null ? "Not available" : `${fmt(t.impressions)} views`, rateLabel: rate === null ? "Not available" : percent(rate) };
    if (rate === null) return { ...base, status: "not_enough" as const, reason: "Not available yet", ratesLabel: null };
    if ((t.impressions ?? 0) < s.minImpressions) return { ...base, status: "not_enough" as const, reason: `Needs ${fmt(s.minImpressions)} views`, ratesLabel: null };
    const v = readTest({ label: t.adName, impressions: t.impressions, clicks: t.clicks }, baseline, s);
    if (v.status === "not_enough") return { ...base, status: "not_enough" as const, reason: rest.length === 0 ? "No other ads to compare with yet" : "Your other ads need more views", ratesLabel: null };
    if (v.status === "too_close") return { ...base, status: "too_close" as const, reason: "Too close to call", ratesLabel: `${base.rateLabel} vs ${percent(clickRate(baseline.impressions, baseline.clicks) ?? 0)}` };
    return v.leader === "a"
      ? { ...base, status: "ahead" as const, reason: "Ahead of your other ads", ratesLabel: v.ratesLabel }
      : { ...base, status: "behind" as const, reason: "Behind your other ads", ratesLabel: v.ratesLabel };
  });
}

// --------------------------------------------------------------------------------------------------------------------
// Weekly swap
// --------------------------------------------------------------------------------------------------------------------

export interface SwapSuggestion {
  replaceKey: string;
  replaceName: string;
  reason: string;
}

/**
 * Suggests replacing the weakest ad once a campaign has the number of ads LinkedIn works best with.
 * Only ads with enough views can be called weakest, and the suggestion is null when none qualifies.
 */
export function suggestSwap(rows: AdWeekRow[], s: InsightSettings = DEFAULT_INSIGHT_SETTINGS): SwapSuggestion | null {
  const ads = totalsPerAd(rows.filter((r) => r.channel === "linkedin"));
  if (ads.length < s.adsPerCampaign) return null;
  const ranked = ads.filter((a) => a.impressions >= s.minImpressions).map((a) => ({ a, rate: a.clicks / a.impressions }));
  if (ranked.length < 2) return null;
  ranked.sort((x, y) => x.rate - y.rate || x.a.adName.localeCompare(y.a.adName));
  const w = ranked[0]!;
  return {
    replaceKey: w.a.adKey,
    replaceName: w.a.adName,
    reason: `Lowest click rate of your ${ads.length} ads: ${percent(w.rate)}`,
  };
}

// --------------------------------------------------------------------------------------------------------------------
// What the admin asks for and gets back
// --------------------------------------------------------------------------------------------------------------------

export const insightAlertSchema = z.object({
  key: z.string(),
  kind: alertKindSchema,
  channel: z.enum(["linkedin", "google_search"]),
  adKey: z.string(),
  title: z.string(),
  facts: z.array(z.string()),
  why: z.string(),
  suggest: z.enum(["pause", "new_angle"]),
});

export const insightSwapSchema = z.object({ replaceKey: z.string(), replaceName: z.string(), reason: z.string() });

export const insightsSchema = z.object({
  product: z.enum(["sonic_hire", "raya"]),
  alerts: z.array(insightAlertSchema),
  swap: insightSwapSchema.nullable(),
  tests: z.array(
    z.object({
      adKey: z.string(),
      adName: z.string(),
      testKind: testKindSchema,
      viewsLabel: z.string(),
      rateLabel: z.string(),
      status: z.enum(["not_enough", "too_close", "ahead", "behind"]),
      reason: z.string(),
      ratesLabel: z.string().nullable(),
    })
  ),
  /** The numbers the rules used, so the screen can say "after 1,000 views". */
  settings: insightSettingsSchema,
});
export type Insights = z.infer<typeof insightsSchema>;

export const alertAnswerBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  product: z.enum(["sonic_hire", "raya"]),
  /** Must be one of the alerts open right now. */
  key: z.string().min(1).max(300),
  answer: alertAnswerSchema,
});
export type AlertAnswerBody = z.infer<typeof alertAnswerBodySchema>;

export const alertAnswerResultSchema = z.object({
  answer: alertAnswerSchema,
  /** When a "Keep going" runs out and the alert may come back. Null for "Pause". */
  revisitAt: z.string().nullable(),
});
export type AlertAnswerResult = z.infer<typeof alertAnswerResultSchema>;

/** Merges saved settings over the starting values. A broken or partly bad saved value falls back to the starting one. */
export function readInsightSettings(saved: string | null | undefined): InsightSettings {
  let obj: Record<string, unknown> = {};
  try {
    const p = JSON.parse(saved || "{}");
    if (p && typeof p === "object" && !Array.isArray(p)) obj = p as Record<string, unknown>;
  } catch {
    /* keep the starting values */
  }
  const out: Record<string, unknown> = { ...DEFAULT_INSIGHT_SETTINGS };
  for (const k of Object.keys(DEFAULT_INSIGHT_SETTINGS) as Array<keyof InsightSettings>) {
    if (!(k in obj)) continue;
    const one = insightSettingsSchema.shape[k].safeParse(obj[k]);
    if (one.success) out[k] = one.data;
  }
  return out as InsightSettings;
}
